import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const navigate = useNavigate();
  const { role, userId, loading: authLoading, loginAsUser } = useAuth();

  const [tab, setTab] = useState('admin');   // 'admin' | 'user'
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // User flow state
  const [phone, setPhone] = useState('');
  const [memberId, setMemberId] = useState('');

  // Redirect if already authenticated
  useEffect(() => {
    if (authLoading) return;
    if (role === 'admin') navigate('/dashboard', { replace: true });
    else if (role === 'user' && userId) navigate(`/user/${userId}`, { replace: true });
  }, [role, userId, authLoading, navigate]);

  // ─── Admin: email + password ───────────────────────────────────────────────
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { email, password } = Object.fromEntries(new FormData(e.target));
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) toast.error('Invalid credentials');
    // On success AuthContext updates role → useEffect above navigates
  };

  // ─── User: verify phone + member ID against users table ──────────────────
  const handleUserLogin = async (e) => {
    e.preventDefault();
    const trimmedPhone = phone.trim();
    const trimmedId = memberId.trim();

    if (!/^\d{10}$/.test(trimmedPhone)) {
      toast.error('Enter a valid 10-digit mobile number');
      return;
    }
    if (!trimmedId) {
      toast.error('Enter your Member ID');
      return;
    }

    setLoading(true);
    const { data, error } = await supabase
      .from('users')
      .select('id')
      .eq('phone', trimmedPhone)
      .eq('id', Number(trimmedId))
      .maybeSingle();
    setLoading(false);

    if (error || !data) {
      toast.error('Mobile number or Member ID is incorrect');
      return;
    }

    loginAsUser(data.id);
    toast.success('Welcome!');
    // useEffect above will navigate to /user/:id
  };

  const st = {
    page: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #0a0a0a 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Segoe UI', sans-serif",
    },
    card: {
      background: '#ffffff', borderRadius: '20px', padding: '40px 40px 48px',
      width: '100%', maxWidth: '420px', boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
    },
    logo: { textAlign: 'center', marginBottom: '28px' },
    coinIcon: {
      width: '96px', height: '96px',
      margin: '0 auto 14px',
      borderRadius: '50%',
      overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    title: { fontSize: '26px', fontWeight: '800', color: '#0f172a', margin: '0 0 4px' },
    subtitle: { fontSize: '14px', color: '#64748b', margin: 0 },
    // Tab switcher
    tabs: {
      display: 'flex', borderRadius: '12px', background: '#f1f5f9',
      padding: '4px', marginBottom: '28px', gap: '4px',
    },
    tabBtn: (active) => ({
      flex: 1, padding: '10px', border: 'none', borderRadius: '9px',
      fontSize: '14px', fontWeight: '700', cursor: 'pointer',
      background: active ? 'linear-gradient(135deg, #C9A84C, #9A7A2C)' : 'transparent',
      color: active ? '#fff' : '#64748b',
      boxShadow: active ? '0 2px 8px rgba(201,168,76,0.35)' : 'none',
      transition: 'all 0.2s',
    }),
    label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' },
    inputGroup: { marginBottom: '20px' },
    input: {
      width: '100%', padding: '12px 14px', border: '1.5px solid #e2e8f0',
      borderRadius: '10px', fontSize: '15px', outline: 'none',
      boxSizing: 'border-box', background: '#f8fafc',
    },
    phoneRow: { display: 'flex', gap: '8px' },
    prefix: {
      padding: '12px 14px', background: '#f1f5f9', border: '1.5px solid #e2e8f0',
      borderRadius: '10px', fontSize: '15px', color: '#475569', fontWeight: '600',
      whiteSpace: 'nowrap', flexShrink: 0,
    },
    btn: (disabled) => ({
      width: '100%', padding: '14px',
      background: disabled ? '#9ca3af' : 'linear-gradient(135deg, #C9A84C, #9A7A2C)',
      color: '#fff', border: 'none', borderRadius: '10px',
      fontSize: '16px', fontWeight: '700', cursor: disabled ? 'not-allowed' : 'pointer',
      marginTop: '8px', boxShadow: disabled ? 'none' : '0 4px 15px rgba(201,168,76,0.3)',
      letterSpacing: '0.3px',
    }),
    backLink: {
      display: 'block', textAlign: 'center', marginTop: '16px',
      fontSize: '13px', color: '#C9A84C', cursor: 'pointer',
      background: 'none', border: 'none', fontWeight: '600',
    },
    otpHint: { fontSize: '13px', color: '#64748b', marginBottom: '20px', lineHeight: '1.5' },
  };

  return (
    <div style={st.page} className="gs-login-page">
      <div style={st.card} className="gs-login-card">
        {/* Logo */}
        <div style={st.logo}>
          <div style={st.coinIcon} className="gs-login-logo">
            <img src="/logo.jpg" alt="Al Ameen" style={{ width: '96px', height: '96px', objectFit: 'cover', borderRadius: '50%', display: 'block' }} />
          </div>
          <h2 style={st.title} className="gs-login-title">Al Ameen Gold & Silver</h2>
          <p style={st.subtitle}>Sign in to your account</p>
        </div>

        {/* Tab switcher */}
        <div style={st.tabs}>
          <button style={st.tabBtn(tab === 'admin')} onClick={() => { setTab('admin'); setPhone(''); setMemberId(''); }}>
            🛡️ Admin
          </button>
          <button style={st.tabBtn(tab === 'user')} onClick={() => { setTab('user'); setPhone(''); setMemberId(''); }}>
            👤 Member
          </button>
        </div>

        {/* ── Admin Login ─────────────────────────────── */}
        {tab === 'admin' && (
          <form onSubmit={handleAdminLogin}>
            <div style={st.inputGroup}>
              <label style={st.label}>Email Address</label>
              <input name="email" type="email" placeholder="Type Email Address..." required
                style={st.input}
                onFocus={e => (e.target.style.borderColor = '#C9A84C')}
                onBlur={e => (e.target.style.borderColor = '#e2e8f0')} />
            </div>
            <div style={st.inputGroup}>
              <label style={st.label}>Password</label>
              <div style={{ position: 'relative' }}>
                <input name="password" type={showPassword ? 'text' : 'password'} placeholder="Type Password..." required
                  autoComplete="new-password"
                  style={{ ...st.input, paddingRight: '42px' }}
                  onFocus={e => (e.target.style.borderColor = '#C9A84C')}
                  onBlur={e => (e.target.style.borderColor = '#e2e8f0')} />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  style={{
                    position: 'absolute', right: '12px', top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#94a3b8', padding: '2px', display: 'flex',
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button type="submit" style={st.btn(loading)} disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In →'}
            </button>
          </form>
        )}

        {/* ── Member Login: phone + member ID ─────────── */}
        {tab === 'user' && (
          <form onSubmit={handleUserLogin}>
            <div style={st.inputGroup}>
              <label style={st.label}>Mobile Number</label>
              <div style={st.phoneRow}>
                <span style={st.prefix}>+91</span>
                <input
                  type="tel" inputMode="numeric" maxLength={10}
                  placeholder="Type Mobile Number..." required
                  value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
                  style={{ ...st.input, flex: 1 }}
                  onFocus={e => (e.target.style.borderColor = '#F59E0B')}
                  onBlur={e => (e.target.style.borderColor = '#e2e8f0')} />
              </div>
            </div>
            <div style={st.inputGroup}>
              <label style={st.label}>Member ID</label>
              <input
                type="tel" inputMode="numeric"
                placeholder="e.g. 12" required
                value={memberId} onChange={e => setMemberId(e.target.value.replace(/\D/g, ''))}
                style={st.input}
                onFocus={e => (e.target.style.borderColor = '#F59E0B')}
                onBlur={e => (e.target.style.borderColor = '#e2e8f0')} />
              <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#94a3b8' }}>
                Your Member ID is shared by your admin when you joined.
              </p>
            </div>
            <button type="submit" style={st.btn(loading)} disabled={loading}>
              {loading ? 'Verifying...' : 'View My Account →'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}